/**
 * @file blendTypes.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-14
 * @modified 2026-08-14
 * @description The blend type vocabulary — what class of stock a blend is for.
 *
 *              Type is not the protein grade. "TMB 18" is a grade (the number in
 *              the name is the protein target); "Milkers" is the type. They vary
 *              independently, so they're separate fields.
 *
 *              The list is derived from how blends are actually named in
 *              inventory.items on the 202 (blends) sales account — Milkers 46,
 *              Beef 23, Rearer 9, Ewe 7, Calf 6, with the rest in single
 *              figures. It's a fixed list rather than free text so the Saved
 *              Blends filter has something stable to group on; "Other" is the
 *              escape hatch, not a reason to add a one-off.
 */

export const BLEND_TYPES = [
  'Milkers',
  'Beef',
  'Calf',
  'Rearer',
  'Heifer / Youngstock',
  'Ewe / Sheep',
  'Dry cow',
  'Other'
];

/** Dropdown options, with a leading blank so type stays optional. */
export const BLEND_TYPE_OPTIONS = [
  { value: '', label: 'Select…' },
  ...BLEND_TYPES.map((t) => ({ value: t, label: t }))
];

/**
 * Guess a type from a blend or item name, for pre-filling only — never for
 * overwriting a type someone has set. Returns '' when nothing matches, which is
 * the common case: most blends are named after the customer and the month, so
 * roughly two thirds of them carry no type word at all.
 *
 * @param {string} name
 * @returns {string} a value from BLEND_TYPES, or ''
 */
export function guessBlendType(name) {
  const n = String(name || '').toLowerCase();
  if (!n) return '';
  if (/milker|dairy/.test(n)) return 'Milkers';
  if (/beef|fatten|finish/.test(n)) return 'Beef';
  if (/calf|calves/.test(n)) return 'Calf';
  if (/rearer|rearing/.test(n)) return 'Rearer';
  if (/heifer|youngstock/.test(n)) return 'Heifer / Youngstock';
  if (/ewe|lamb|sheep/.test(n)) return 'Ewe / Sheep';
  if (/dry ?cow|transition/.test(n)) return 'Dry cow';
  return '';
}
